import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';

const STATUS_OPTIONS = ['', 'ABERTA', 'CUMPRIDA', 'QUEBRADA', 'CANCELADA'];
const CHANGE_STATUS_OPTIONS = ['CUMPRIDA', 'QUEBRADA', 'CANCELADA'];

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatDate = (value) => {
  if (!value) return '-';
  const text = String(value);
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return text;
  return dt.toLocaleDateString('pt-BR');
};

const statusBadge = (status) => {
  if (status === 'CUMPRIDA') return 'badge-success';
  if (status === 'ABERTA') return 'badge-warning';
  if (status === 'QUEBRADA') return 'badge-danger';
  return 'badge-info';
};

const CollectionsPromisesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    q: '',
  });

  const loadPromises = async () => {
    setLoading(true);
    try {
      const params = {
        pageSize: 200,
      };
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;

      const { data } = await axios.get('/api/collections/promises', { params });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar acordos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPromises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.q]);

  const changeStatus = async (promiseId, status) => {
    try {
      await axios.post(`/api/collections/promises/${promiseId}/status`, { status });
      toast.success(`Acordo marcado como ${status}`);
      await loadPromises();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar acordo');
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <section className="rounded-2xl border border-amber-300 bg-gradient-to-r from-slate-900 via-amber-900 to-orange-900 p-6 text-white shadow-soft">
        <h1 className="text-3xl font-bold">Acordos de Pagamento</h1>
        <p className="mt-2 text-amber-100">Controle de promessas ativas, quebradas e cumpridas com trilha de auditoria.</p>
      </section>

      <div className="card">
        <div className="card-body p-3">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select className="form-select text-sm py-1.5" value={filters.status} onChange={(e) => setFilters((old) => ({ ...old, status: e.target.value }))}>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status || 'all'} value={status}>
                    {status || 'Todos'}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-8">
              <label className="block text-xs font-medium text-gray-600 mb-1">Busca</label>
              <input className="form-input text-sm py-1.5" value={filters.q} onChange={(e) => setFilters((old) => ({ ...old, q: e.target.value }))} placeholder="Cliente ou id da cobranca" />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button className="btn-secondary text-sm py-1.5 px-3" onClick={() => setFilters({ status: '', q: '' })}>
                Limpar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <span>Acordos ({items.length})</span>
          {loading && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Modulo</th>
                  <th>Vencimento promessa</th>
                  <th>Valor prometido</th>
                  <th>Status</th>
                  <th>Caso</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="font-medium">{item.customer_nome || '-'}</td>
                    <td>{item.source_module || '-'}</td>
                    <td>{formatDate(item.due_date)}</td>
                    <td className="font-semibold">{formatMoney(item.promised_amount || 0)}</td>
                    <td>
                      <span className={statusBadge(item.status)}>{item.status}</span>
                    </td>
                    <td>
                      <span className="text-xs text-gray-600">
                        {item.case_status} | Risco {Number(item.risco_score || 0).toFixed(1)}
                      </span>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-2 justify-end">
                        {item.status === 'ABERTA' &&
                          CHANGE_STATUS_OPTIONS.map((status) => (
                            <button key={status} className="btn-secondary text-xs py-1.5 px-2" onClick={() => changeStatus(item.id, status)}>
                              {status}
                            </button>
                          ))}
                        <Link to={`/cobrancas/${item.charge_id}`} className="btn-primary text-xs py-1.5 px-2">
                          Cobranca
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-gray-500">
                      Nenhum acordo encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectionsPromisesPage;
